import React, { useEffect, useState } from 'react';
import { companyAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Wallet, Loader2, Plus, Pencil, CreditCard, Landmark, AlertTriangle, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function CompanyWallets() {
  const [wallets, setWallets] = useState([]);
  const [subWallets, setSubWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subLimit, setSubLimit] = useState(10);
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [renaming, setRenaming] = useState(false);

  const load = async () => {
    try {
      const [w, sw] = await Promise.all([
        companyAdminAPI.getWallets(),
        companyAdminAPI.getSubWallets().catch(() => ({ wallets: [], limit: 10 })),
      ]);
      setWallets(w);
      setSubWallets(sw.wallets || []);
      setSubLimit(sw.limit || 10);
    } catch { toast.error('Failed to load wallets'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    try {
      await companyAdminAPI.createSubWallet({ label: label.trim() });
      toast.success(`Sub-wallet "${label.trim()}" created`);
      setLabel('');
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create sub-wallet');
    } finally { setCreating(false); }
  };

  const handleRename = async (walletId) => {
    if (!editLabel.trim()) return;
    setRenaming(true);
    try {
      await companyAdminAPI.renameSubWallet(walletId, { label: editLabel.trim() });
      toast.success('Sub-wallet renamed');
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    } finally { setRenaming(false); }
  };

  const copyNumber = (num) => {
    navigator.clipboard.writeText(num);
    toast.success('Account number copied');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-8" data-testid="company-wallets">
      {/* Main Wallets */}
      <div>
        <div className="mb-3"><h1 className="text-xl font-bold text-gray-900">Wallets</h1><p className="text-sm text-gray-500">{wallets.length} wallet{wallets.length !== 1 ? 's' : ''}</p></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {wallets.map(w => (
            <Card key={w.id} className="border-gray-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Wallet className="w-4 h-4" /></div>
                  <span className="text-xs text-gray-500 uppercase font-medium">{w.label || w.wallet_type} wallet</span>
                </div>
                <p className="text-xl font-bold text-gray-900">NGN {Number(w.balance || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-1">{w.owner_type} | {w.status}</p>
              </CardContent>
            </Card>
          ))}
          {wallets.length === 0 && <p className="text-sm text-gray-400 col-span-3 text-center py-8">No wallets found</p>}
        </div>
      </div>

      {/* Sub-Wallets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Sub-Wallets</h2>
            <p className="text-xs text-gray-400">{subWallets.length} of {subLimit} used</p>
          </div>
          {subWallets.length < subLimit && (
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="ca-create-sub-wallet-btn"
              className="h-8 bg-pink-500 hover:bg-pink-600 text-white text-xs">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Sub-Wallet
            </Button>
          )}
        </div>

        {subWallets.length === 0 ? (
          <Card className="border-dashed border-gray-300">
            <CardContent className="p-8 text-center space-y-2">
              <CreditCard className="w-8 h-8 text-gray-300 mx-auto" />
              <p className="text-sm text-gray-500">No sub-wallets yet</p>
              <p className="text-xs text-gray-400">Create sub-wallets to ring-fence funds for specific projects or purposes</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subWallets.map(w => (
              <Card key={w.id} className="border-gray-200 shadow-sm hover:border-indigo-200 transition-colors" data-testid={`ca-sub-wallet-${w.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <CreditCard className="w-4 h-4 text-indigo-500" />
                      </div>
                      {editingId === w.id ? (
                        <div className="flex items-center gap-1.5">
                          <Input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                            className="h-7 text-xs w-28" autoFocus data-testid="ca-rename-input" />
                          <Button size="sm" onClick={() => handleRename(w.id)} disabled={renaming}
                            className="h-7 px-2 bg-indigo-500 text-white text-[10px]">
                            {renaming ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7 px-1 text-[10px]">x</Button>
                        </div>
                      ) : (
                        <div>
                          <span className="text-sm font-semibold text-gray-900">{w.label}</span>
                          <button onClick={() => { setEditingId(w.id); setEditLabel(w.label); }}
                            className="ml-1.5 text-gray-400 hover:text-gray-600" data-testid={`ca-rename-${w.id}`}>
                            <Pencil className="w-3 h-3 inline" />
                          </button>
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${w.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                      {w.status}
                    </span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">NGN {Number(w.balance || 0).toLocaleString()}</p>
                  {w.virtual_account_number ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Landmark className="w-3 h-3 text-emerald-500" />
                      <span className="font-mono">{w.virtual_account_number}</span>
                      <button onClick={() => copyNumber(w.virtual_account_number)} className="text-gray-400 hover:text-gray-600">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-[10px] text-orange-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No virtual account</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">Create Sub-Wallet</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Wallet Name</label>
              <Input placeholder="e.g. March Stipends, Project X" value={label} data-testid="ca-sub-wallet-label"
                onChange={e => setLabel(e.target.value)} required maxLength={100} />
            </div>
            <p className="text-[10px] text-gray-400">A virtual account will be automatically provisioned for this sub-wallet.</p>
            <Button type="submit" disabled={creating || !label.trim()} data-testid="ca-confirm-create"
              className="w-full bg-pink-500 hover:bg-pink-600 text-white">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Sub-Wallet'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
